import { useEffect, useState } from "react";
import { doc, onSnapshot } from "firebase/firestore";
import { useUser } from "../../../contexts/UserContext";
import { Family } from "../../types/Family";
import { firebaseDB } from "../../../infrastructure/firebase/firebase";

interface UseFamilyResult {
  family: Family | null;
  loading: boolean;
  error: Error | null;
}

export const useFamily = (): UseFamilyResult => {
  const { appUser, loading: userLoading } = useUser();
  const [family, setFamily] = useState<Family | null>(null);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<Error | null>(null);

  useEffect(() => {
    let unsubscribe: (() => void) | null = null;

    if (userLoading) {
      setLoading(true);
      return;
    }

    if (appUser && appUser.familyId) {
      const familyDocRef = doc(firebaseDB, "families", appUser.familyId);

      unsubscribe = onSnapshot(
        familyDocRef,
        (docSnapshot) => {
          if (docSnapshot.exists()) {
            const familyData = docSnapshot.data() as Family;
            setFamily(familyData);
          } else {
            setFamily(null);
          }
          setLoading(false);
          setError(null);
        },
        (err) => {
          console.error("Error fetching family data:", err);
          setError(err);
          setLoading(false);
        },
      );
    } else {
      // User does not have a familyId
      setFamily(null);
      setLoading(false);
    }

    return () => {
      if (unsubscribe) {
        unsubscribe();
      }
    };
  }, [appUser, userLoading]);

  return { family, loading, error };
};
